// CreateMedicalProfessional.js - COMPLETE FILE with proper facility handling and AuthContext integration

import React, { useState, useEffect } from "react";
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Phone as PhoneIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { medicalProfessionalsService } from "../../services/medicalProfessionalsService";
import { useAuth } from "../../contexts/AuthContext";

const CreateMedicalProfessional = () => {
  const navigate = useNavigate();
  const { user, canManageMedicalProfessionals } = useAuth();
  const [loading, setLoading] = useState(false);
  const [facilities, setFacilities] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Form data
  const [formData, setFormData] = useState({
    full_name: "",
    username: "",
    email: "",
    password: "",
    confirm_password: "",
    phone_number: "",
    mp_category: "",
    medical_license_number: "",
    npi_number: "",
    facility_id: "",
    send_activation_email: false,
  });

  // Validation errors
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchFacilities();
  }, []);

  const fetchFacilities = async () => {
    try {

      // FIXED: For facility admins, just use their assigned facility
      if (user?.role === "facility_admin" && user?.facility_id) {
        // Create a mock facility object for facility admin
        const userFacility = {
          id: user.facility_id,
          company_name: user.company_name || `Facility ${user.facility_id}`,
          address: user.address || "Address not available",
        };

        setFacilities([userFacility]);
        setFormData((prev) => ({
          ...prev,
          facility_id: user.facility_id,
        }));

        return;
      }

      // For super admins, fetch all facilities
      const { facilityService } = await import(
        "../../services/facilityService"
      );
      const response = await facilityService.getFacilities();
      setFacilities(response || []);

      // Auto-select facility if only one available
      if (response?.length === 1) {
        setFormData((prev) => ({
          ...prev,
          facility_id: response[0].id,
        }));
      }
    } catch (err) {
      console.error("Error fetching facilities:", err);

      // FIXED: For facility admins, create a fallback facility
      if (user?.role === "facility_admin" && user?.facility_id) {
        const fallbackFacility = {
          id: user.facility_id,
          company_name: user.company_name || "Your Facility",
          address: "Address not available",
        };

        setFacilities([fallbackFacility]);
        setFormData((prev) => ({
          ...prev,
          facility_id: user.facility_id,
        }));

        setError(
          "Unable to load facility details, but using your assigned facility."
        );
      } else {
        setError("Failed to load facilities. Please try again.");
      }
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }

    // Clear general error
    if (error) {
      setError("");
    }
  };

  const validateForm = () => {
    const errors = {};

    // Required fields
    if (!formData.full_name?.trim()) {
      errors.full_name = "Full name is required";
    }

    if (!formData.username?.trim()) {
      errors.username = "Username is required";
    } else if (formData.username.length < 3) {
      errors.username = "Username must be at least 3 characters";
    }

    if (!formData.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!formData.phone_number?.trim()) {
      errors.phone_number = "Phone number is required for SMS verification";
    } else if (!/^\+?[\d\s\-\(\)]{10,}$/.test(formData.phone_number.trim())) {
      errors.phone_number = "Please enter a valid phone number";
    }

    if (!formData.mp_category) {
      errors.mp_category = "Category is required";
    }

    // FIXED: For facility admins, facility_id is auto-assigned, so don't validate it
    if (user?.role !== "facility_admin" && !formData.facility_id) {
      errors.facility_id = "Facility is required";
    }

    // Password validation (only if not sending activation email)
    if (!formData.send_activation_email) {
      if (!formData.password?.trim()) {
        errors.password =
          "Password is required when not sending activation email";
      } else if (formData.password.length < 6) {
        errors.password = "Password must be at least 6 characters";
      }

      if (formData.password !== formData.confirm_password) {
        errors.confirm_password = "Passwords don't match";
      }
    }

    // NPI validation (if provided)
    if (formData.npi_number && !/^\d{10}$/.test(formData.npi_number.trim())) {
      errors.npi_number = "NPI must be exactly 10 digits";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Please fix the validation errors below");
      return;
    }

    setLoading(true);
    setError("");

    try {

      // FIXED: Prepare data for API - don't include facility_id for facility admins (backend will handle it)
      const apiData = {
        full_name: formData.full_name.trim(),
        username: formData.username.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        phone_number: formData.phone_number.trim(),
        mp_category: formData.mp_category,
        medical_license_number: formData.medical_license_number?.trim() || null,
        npi_number: formData.npi_number?.trim() || null,
        send_activation_email: formData.send_activation_email,
      };

      // Only include facility_id for super admins (facility admins get auto-assigned)
      if (user?.role === "super_admin" && formData.facility_id) {
        apiData.facility_id = formData.facility_id;
      }


      const response =
        await medicalProfessionalsService.createCustomMedicalProfessional(
          apiData
        );


      setSuccess("Medical professional created successfully!");
      setSnackbarMessage(
        `Medical professional created successfully in ${
          facilities.find(
            (f) => f.id === (response.facility_id || formData.facility_id)
          )?.company_name || "your facility"
        }!`
      );
      setSnackbarOpen(true);

      // Redirect after a short delay
      setTimeout(() => {
        navigate("/medical-professionals");
      }, 2000);
    } catch (err) {
      console.error("Error creating medical professional:", err);

      // FIXED: Enhanced error handling with facility-specific messages
      const errorMessage = medicalProfessionalsService.getFacilityErrorMessage(
        err,
        "create"
      );

      if (err.message?.includes("Email already registered")) {
        setError(
          "This email is already registered. Please use a different email address."
        );
        setValidationErrors((prev) => ({
          ...prev,
          email: "Email already exists",
        }));
      } else if (err.message?.includes("Username")) {
        setError(
          "This username is already taken. Please choose a different username."
        );
        setValidationErrors((prev) => ({
          ...prev,
          username: "Username already exists",
        }));
      } else if (err.message?.includes("phone number")) {
        setError(
          "Invalid phone number format. Please enter a valid phone number."
        );
        setValidationErrors((prev) => ({
          ...prev,
          phone_number: "Invalid phone number",
        }));
      } else if (err.message?.includes("facility")) {
        setError(
          "There was an issue with the facility assignment. Please contact support."
        );
      } else if (err.message?.includes("permission")) {
        setError(
          "You don't have permission to create medical professionals. Please contact your administrator."
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Check permissions properly using AuthContext
  if (!canManageMedicalProfessionals) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }} icon={<WarningIcon />}>
          You don't have permission to create medical professionals. Please
          contact your administrator.
        </Alert>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate("/medical-professionals")}
        >
          Back to Medical Professionals
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={4}>
        <Button
          onClick={() => navigate("/medical-professionals")}
          startIcon={<BackIcon />}
          sx={{ mr: 2 }}
        >
          Back to Medical Professionals
        </Button>
        <Typography variant="h4" fontWeight="bold">
          Create Medical Professional
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          {/* Creation Mode */}
          <Card variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
            <CardHeader title="Creation Mode" sx={{ pb: 1 }} />
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={!formData.send_activation_email}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        send_activation_email: !e.target.checked,
                      }))
                    }
                    color="primary"
                  />
                }
                label="Create new user account with medical professional"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.send_activation_email}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        send_activation_email: e.target.checked,
                      }))
                    }
                    color="primary"
                  />
                }
                label="Send activation email instead of setting password now"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {formData.send_activation_email
                  ? "The medical professional will receive an email to activate their account and set their password. They will need to verify their phone number for SMS two-factor authentication on first login."
                  : "You will set the password now for immediate access. The phone number will be used for SMS two-factor authentication."}
              </Typography>
            </CardContent>
          </Card>

          {/* User Information */}
          <Card variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center" gap={1}>
                  <PersonIcon color="primary" />
                  <Typography variant="h6" color="primary">
                    User Information
                  </Typography>
                </Box>
              }
              sx={{ pb: 1 }}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Full Name *"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    error={!!validationErrors.full_name}
                    helperText={validationErrors.full_name}
                    disabled={loading}
                  />
                </Grid>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Username *"
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                    error={!!validationErrors.username}
                    helperText={validationErrors.username || "For system login"}
                    disabled={loading}
                  />
                </Grid>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Email *"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!validationErrors.email}
                    helperText={validationErrors.email}
                    disabled={loading}
                  />
                </Grid>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Phone Number *"
                    name="phone_number"
                    type="tel"
                    value={formData.phone_number}
                    onChange={handleChange}
                    error={!!validationErrors.phone_number}
                    helperText={
                      validationErrors.phone_number ||
                      "Required for SMS two-factor authentication"
                    }
                    disabled={loading}
                    placeholder="+1 (555) 123-4567"
                    InputProps={{
                      startAdornment: (
                        <PhoneIcon
                          sx={{ color: "action.active", mr: 1, my: 0.5 }}
                        />
                      ),
                    }}
                  />
                </Grid>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth error={!!validationErrors.mp_category}>
                    <InputLabel>Category *</InputLabel>
                    <Select
                      name="mp_category"
                      value={formData.mp_category}
                      onChange={handleChange}
                      label="Category *"
                      disabled={loading}
                    >
                      <MenuItem value="nurse">Nurse</MenuItem>
                      <MenuItem value="phlebotomist">Phlebotomist</MenuItem>
                      <MenuItem value="technician">Technician</MenuItem>
                      <MenuItem value="doctor">Physician Assistant</MenuItem>
                      <MenuItem value="therapist">Therapist</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                    {validationErrors.mp_category && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.5 }}
                      >
                        {validationErrors.mp_category}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Professional Information */}
          <Card variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
            <CardHeader
              title={
                <Box display="flex" alignItems="center" gap={1}>
                  <BusinessIcon color="primary" />
                  <Typography variant="h6" color="primary">
                    Professional Information
                  </Typography>
                </Box>
              }
              sx={{ pb: 1 }}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Medical License Number"
                    name="medical_license_number"
                    value={formData.medical_license_number}
                    onChange={handleChange}
                    error={!!validationErrors.medical_license_number}
                    helperText={
                      validationErrors.medical_license_number ||
                      "Optional - Enter if applicable"
                    }
                    disabled={loading}
                  />
                </Grid>
                <Grid  size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="NPI Number"
                    name="npi_number"
                    value={formData.npi_number}
                    onChange={handleChange}
                    error={!!validationErrors.npi_number}
                    helperText={
                      validationErrors.npi_number ||
                      "Optional - 10 digits, not all professionals have NPIs"
                    }
                    disabled={loading}
                    inputProps={{
                      maxLength: 10,
                      pattern: "[0-9]*",
                    }}
                  />
                </Grid>

                {/* FIXED: Facility Selection with proper admin handling */}
                <Grid  size={{ xs: 12 }}>
                  <FormControl fullWidth error={!!validationErrors.facility_id}>
                    <InputLabel>
                      Facility{" "}
                      {user?.role !== "facility_admin"
                        ? "*"
                        : "(Auto-assigned)"}
                    </InputLabel>
                    <Select
                      name="facility_id"
                      value={formData.facility_id}
                      onChange={handleChange}
                      label={
                        user?.role !== "facility_admin"
                          ? "Facility *"
                          : "Facility (Auto-assigned)"
                      }
                      disabled={loading || user?.role === "facility_admin"}
                    >
                      {facilities.length === 0 && (
                        <MenuItem disabled value="">
                          <em>Loading facilities...</em>
                        </MenuItem>
                      )}
                      {facilities.map((facility) => (
                        <MenuItem key={facility.id} value={facility.id}>
                          {facility.company_name}{" "}
                          {facility.address ? `(${facility.address})` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                    {user?.role === "facility_admin" && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, ml: 1.5 }}
                      >
                        Medical professionals will be automatically assigned to
                        your facility
                      </Typography>
                    )}
                    {validationErrors.facility_id && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, ml: 1.5 }}
                      >
                        {validationErrors.facility_id}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Security Information */}
          {!formData.send_activation_email && (
            <Card variant="outlined" sx={{ mb: 4, borderRadius: 2 }}>
              <CardHeader
                title={
                  <Box display="flex" alignItems="center" gap={1}>
                    <SecurityIcon color="primary" />
                    <Typography variant="h6" color="primary">
                      Security Information
                    </Typography>
                  </Box>
                }
                sx={{ pb: 1 }}
              />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid  size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Password *"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      error={!!validationErrors.password}
                      helperText={
                        validationErrors.password || "Minimum 6 characters"
                      }
                      disabled={loading}
                    />
                  </Grid>
                  <Grid  size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Confirm Password *"
                      name="confirm_password"
                      type="password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      error={!!validationErrors.confirm_password}
                      helperText={
                        validationErrors.confirm_password ||
                        "Must match password above"
                      }
                      disabled={loading}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Facility Assignment Info */}
          {user?.role === "facility_admin" && (
            <Alert severity="info" icon={<BusinessIcon />} sx={{ mb: 4 }}>
              <Typography variant="body2">
                <strong>Facility Assignment:</strong> As a facility
                administrator, medical professionals you create will be
                automatically assigned to your facility.
                {user.company_name && ` (${user.company_name})`}
              </Typography>
            </Alert>
          )}

          {/* SMS 2FA Information Alert */}
          <Alert severity="info" icon={<PhoneIcon />} sx={{ mb: 4 }}>
            <Typography variant="body2">
              <strong>SMS Two-Factor Authentication:</strong> The phone number
              will be used for SMS-based two-factor authentication.
              {formData.send_activation_email
                ? " The medical professional will need to verify their phone number during account activation."
                : " The medical professional will need to verify their phone number on their first login."}
            </Typography>
          </Alert>

          {/* General Info Alert */}
          <Alert severity="info" sx={{ mb: 4 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Name, Username, Email, Phone Number,
              Category are required. Phone number is needed for SMS two-factor
              authentication. License and NPI numbers are optional as not all
              medical professionals have these credentials.
              {formData.send_activation_email &&
                " An activation email will be sent to the provided email address."}
              {user?.role === "facility_admin" &&
                " The medical professional will be automatically assigned to your facility."}
            </Typography>
          </Alert>

          {/* Submit Button */}
          <Box display="flex" justifyContent="flex-end" gap={2}>
            <Button
              variant="outlined"
              onClick={() => navigate("/medical-professionals")}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              startIcon={
                loading ? <CircularProgress size={16} /> : <SaveIcon />
              }
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "1.1rem",
                fontWeight: 600,
              }}
            >
              {loading ? "Creating..." : "Create Medical Professional"}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default CreateMedicalProfessional;
